import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import { Card, CardBody, Col, Row, UncontrolledTooltip, Pagination, PaginationItem, PaginationLink, Input, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import komponen lokal
import TableContainer from '../../components/Common/TableContainer';
import Spinners from "components/Common/Spinner";
import Breadcrumbs from '../../components/Common/Breadcrumb';

// Import fungsi layanan (service)
import { fetchPayments, fetchPaymentsByStatus } from '../../services/paymentService';

const Payments = () => {
    document.title = "Daftar Pembayaran";

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });

    const [inputFilters, setInputFilters] = useState({ search: "", order_id: "", snap_token: "", status: "", email: "" });
    const [filters, setFilters] = useState({ search: "", order_id: "", snap_token: "", status: "", email: "" });
    const [totalSettlement, setTotalSettlement] = useState(0);

    // Ambil data total settlement (dijalankan sekali saat komponen dimuat)
    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const responseSummary = await fetchPaymentsByStatus({ status: 'settlement' });
                const summarySettlement = responseSummary.data.find(item => item.status?.toUpperCase() === 'SETTLEMENT');
                setTotalSettlement(summarySettlement ? summarySettlement.total : 0);
            } catch (error) {
                console.error("Error fetching summary:", error);
                toast.error("Gagal memuat ringkasan pembayaran.");
            }
        };
        fetchSummary();
    }, []);

    // Ambil data untuk tabel (dijalankan saat page atau filter berubah)
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const responsePayments = await fetchPayments(pagination.page, pagination.per_page, filters);
                setData(responsePayments.data);
                setPagination(responsePayments.meta);
            } catch (error) {
                console.error("Error in fetchData:", error);
                toast.error(error.message || "Gagal memuat data pembayaran.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.page, filters.search, filters.order_id, filters.snap_token, filters.status, filters.email]);

    const handleApplyFilters = () => {
        setFilters(inputFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleResetFilters = () => {
        setInputFilters({ search: "", order_id: "", snap_token: "", status: "", email: "" });
        setFilters({ search: "", order_id: "", snap_token: "", status: "", email: "" });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const formatRupiah = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const columns = useMemo(
        () => [
            { header: "Order ID", accessorKey: "order_id", enableColumnFilter: false },
            { header: "Nama Produk", accessorKey: "produk.nama_produk", enableColumnFilter: false, cell: ({ row }) => row.original.produk?.nama_produk || "-" },
            { header: "Kategori Paket", accessorKey: "produk.kategori_paket", enableColumnFilter: false, cell: ({ row }) => row.original.produk?.kategori_paket || "-" },
            { header: "Email", accessorKey: "email", enableColumnFilter: false },
            { header: "Jumlah", accessorKey: "amount", enableColumnFilter: false, cell: ({ getValue }) => formatRupiah(getValue()) },
            { header: "Tipe Pembayaran", accessorKey: "payment_type", enableColumnFilter: false, cell: ({ getValue }) => getValue()?.replace(/_/g, ' ').toUpperCase() || '-' },
            {
                header: "Status",
                accessorKey: "status",
                enableColumnFilter: false,
                cell: ({ getValue }) => (
                    <span className={`badge ${
                        getValue()?.toUpperCase() === 'SETTLEMENT' ? 'bg-success' :
                        getValue()?.toUpperCase() === 'PENDING' ? 'bg-warning' : 'bg-danger'
                    }`}>
                        {getValue()?.toUpperCase() || '-'}
                    </span>
                ),
            },
            {
                header: "Tanggal Transaksi",
                accessorKey: "created_at",
                enableColumnFilter: false,
                cell: ({ getValue }) => new Date(getValue()).toLocaleDateString('id-ID', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
            },
            {
                header: "Aksi",
                accessorKey: "aksi",
                enableColumnFilter: false,
                cell: ({ row }) => (
                    <div>
                        <a
                            href={`https://app.sandbox.midtrans.com/snap/v4/redirection/${row.original.snap_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                        >
                            Lihat Transaksi
                        </a>
                    </div>
                ),
            },
        ],
        []
    );

    const CustomPagination = () => {
        const { page, last_page, total, per_page } = pagination;
        if (total === 0) return null;

        const getPageNumbers = () => {
            const pages = [];
            const maxVisiblePages = 5;
            let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

            if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(
                    <PaginationItem key={i} active={i === page}>
                        <PaginationLink onClick={() => setPagination(prev => ({ ...prev, page: i }))}>{i}</PaginationLink>
                    </PaginationItem>
                );
            }
            return pages;
        };

        return (
            <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted">
                    Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
                    <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{' '}
                    <span className="fw-semibold">{total}</span> data
                </div>
                <div className="d-flex align-items-center">
                    <Pagination className="pagination pagination-rounded mb-0">
                        <PaginationItem disabled={page === 1}>
                            <PaginationLink previous onClick={() => page > 1 && setPagination(prev => ({ ...prev, page: page - 1 }))}>
                                &lt;
                            </PaginationLink>
                        </PaginationItem>
                        {getPageNumbers()}
                        <PaginationItem disabled={page === last_page}>
                            <PaginationLink next onClick={() => page < last_page && setPagination(prev => ({ ...prev, page: page + 1 }))}>
                                &gt;
                            </PaginationLink>
                        </PaginationItem>
                    </Pagination>
                </div>
            </div>
        );
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title="Pembayaran" breadcrumbItem="List Pembayaran" />
                <Row>
                    <Col xl={12}>
                        <Row>
                            <Col md={12}>
                                <Card className="mini-stats-wid">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="text-muted fw-medium mb-1">Total Pembayaran Sukses</p>
                                                <h4 className="mb-0 text-success fw-bold">{formatRupiah(totalSettlement)}</h4>
                                            </div>
                                            <div className="mini-stat-icon avatar-sm rounded-circle bg-success align-self-center">
                                                <span className="avatar-title">
                                                    <i className="bx bx-check-circle font-size-24"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg="12">
                        <Card>
                            <CardBody className="border-bottom">
                                <div className="d-flex align-items-center justify-content-between">
                                    <h5 className="mb-0 card-title flex-grow-1">Daftar Semua Transaksi</h5>
                                    <div className="d-flex justify-content-end mb-3">
                                        <div className="search-box me-3">
                                            <div className="position-relative">
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Cari Order ID..."
                                                    name="order_id"
                                                    value={inputFilters.order_id}
                                                    onChange={(e) => setInputFilters(prev => ({ ...prev, order_id: e.target.value }))}
                                                />
                                                <i className="bx bx-search-alt search-icon" />
                                            </div>
                                        </div>
                                        {/* Tambahan: Input filter berdasarkan Email */}
                                        <div className="search-box me-3">
                                            <div className="position-relative">
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Cari Email..."
                                                    name="email"
                                                    value={inputFilters.email}
                                                    onChange={(e) => setInputFilters(prev => ({ ...prev, email: e.target.value }))}
                                                />
                                                <i className="bx bx-search-alt search-icon" />
                                            </div>
                                        </div>
                                        {/* Akhir Tambahan */}
                                        <Button color="primary" className="ms-2" onClick={handleApplyFilters}>
                                            <i className="bx bx-search me-1"></i> Terapkan
                                        </Button>
                                        <Button
                                            color="danger"
                                            outline
                                            onClick={handleResetFilters}
                                            className="ms-2"
                                        >
                                            <i className="mdi mdi-refresh me-1"></i> Reset
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                            <CardBody>
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinners />
                                    </div>
                                ) : data.length > 0 ? (
                                    <>
                                        <TableContainer
                                            columns={columns}
                                            data={data}
                                            isGlobalFilter={false}
                                            isPagination={false}
                                            customPageSize={pagination.per_page}
                                            tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                                        />
                                        <CustomPagination />
                                    </>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        Tidak ada data pembayaran yang tersedia.
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
            <ToastContainer />
        </div>
    );
};

export default Payments;